/*
 * fn: getPriceSourceLink
 *
 * Gets a link to a card's listing for a price source, falling back to a search when there is no slug
 *
 */
import { type UtmType } from '@/defs/misc'
import { type PriceSourceType } from '@/defs/price'
import { getUtmLink } from '@/utils/commonUtil'

import { getImpactLink } from './getImpactLink'

type Props = {
  name: string
  priceSource: PriceSourceType
  slug?: string
}

type ConfigType = {
  getHref: (props: Props) => string
  utm?: UtmType | false
}

const getSearchSlug = (name: string) =>
  encodeURIComponent(
    name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace('꞉', ':') /* Ratonhnhaké꞉ton uses a "colon sign" which is NOT a colon */,
  )

const CONFIG: Record<PriceSourceType, ConfigType> = {
  cardhoarder: {
    getHref: ({ name, slug }) =>
      slug
        ? `https://www.cardhoarder.com/cards/${slug}?affiliate_id=edhrec`
        : `https://www.cardhoarder.com/cards?cardname=${getSearchSlug(name)}`,
  },
  cardkingdom: {
    getHref: ({ name, slug }) =>
      slug
        ? `https://www.cardkingdom.com/${slug}?partner=edhrec`
        : `https://www.cardkingdom.com/catalog/search?filter%5Bname%5D=${getSearchSlug(name)}`,
    utm: { campaign: 'edhrec', medium: 'affiliate' },
  },
  cardmarket: {
    getHref: ({ name, slug }) =>
      slug
        ? `https://www.cardmarket.com/en/Magic/Products/Search?searchString=${slug}`
        : `https://www.cardmarket.com/en/Magic/Products/Search?searchString=${getSearchSlug(name)}`,
    utm: { campaign: 'card_prices', medium: 'text' },
  },
  cardsphere: {
    getHref: ({ name, slug }) =>
      slug ? `https://www.cardsphere.com/cards/${slug}` : `https://www.cardsphere.com/search?q=${getSearchSlug(name)}`,
    utm: { medium: 'text' },
  },
  cardtrader: {
    getHref: ({ name, slug }) =>
      slug
        ? `https://www.cardtrader.com/en/cards/${slug}?share_code=SpaceCowMedia`
        : `https://www.cardtrader.com/en/?q=${getSearchSlug(name)}`,
    utm: { medium: 'text' },
  },
  face2face: {
    getHref: ({ name }) => `https://www.facetofacegames.com/search?q=${name}&dt_id=2364253`,
    utm: { medium: 'affiliate' },
  },
  manapool: {
    getHref: ({ name, slug }) =>
      slug
        ? `https://manapool.com/card/${slug}?ref=scm&ref_meta=referrer:edhrec,cardprice`
        : `https://manapool.com/cards?q=${getSearchSlug(name)}`,
    utm: { medium: 'affiliate' },
  },
  mtgstocks: {
    getHref: ({ slug }) => (slug ? `https://www.mtgstocks.com/prints/${slug}` : 'https://www.mtgstocks.com'),
  },
  scg: {
    getHref: ({ name }) =>
      getImpactLink({
        brand: 'scg',
        subId1: 'edhrec,cardprice',
        u: `https://starcitygames.com/search/?item_display_name=${getSearchSlug(name)}`,
      }),
    utm: false,
  },
  tcgplayer: {
    getHref: ({ name, slug }) =>
      getImpactLink({
        brand: 'tcgplayer',
        subId1: 'edhrec,cardprice',
        u: slug
          ? `https://www.tcgplayer.com/product/${slug}`
          : `https://www.tcgplayer.com/search/all/product?q=${getSearchSlug(name)}`,
      }),
    utm: false,
  },
}

export const getPriceSourceLink = (props: Props) => {
  const { getHref, utm } = CONFIG[props.priceSource]
  const href = getHref(props)

  return utm === false ? href : getUtmLink(href, utm)
}
